/**
 * Single source of truth for the settling-in progress tri-state indicator.
 *
 * Mirrors `lib/capacityColor.ts`'s shape, and — crucially — reuses its
 * already-gated `--capacity-*` two-role split rather than introducing a
 * `settling-*` token family:
 *
 *   - fills bind to `--capacity-{normal,warning,critical}`
 *   - text/border bind to `--capacity-{normal,warning,critical}-on-surface`
 *
 * That split is what `capacityColor.contrast.test.ts` measures across all 8 theme
 * blocks x 4 surfaces, so every colour this module renders is already gated and
 * `styles/index.css` / `tailwind.config.js` stay untouched. `--success` and
 * `--destructive` are NOT usable here: they have no `-on-surface` role, and
 * `--success: 142 76% 36%` as text on `--card` fails WCAG 1.4.3.
 *
 * A sibling test (`settlingInIndicator.contrast.test.ts`) pins that binding.
 *
 * Every value is distinguishable WITHOUT perceiving hue: `shape` (border-radius
 * + border-style) is unique per value, and the glyph differs. In particular
 * `unset` differs from `neutral` on three independent channels — hue (muted vs
 * amber), border-style (dashed vs solid) and glyph (— vs 😐) — because "not yet
 * assessed" and "assessed as neutral" are different domain facts.
 */

import { Frown, Meh, Minus, Smile } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { msg } from '@lingui/macro';
import type { MessageDescriptor } from '@lingui/core';

export type IndicatorValue = 'good' | 'neutral' | 'bad' | 'unset';

export interface IndicatorStyle {
  /** The resolved value, exposed for callers that drive their own layout. */
  value: IndicatorValue;
  /** Surface + border fill for the chip. */
  chip: string;
  /** The gated on-surface foreground. */
  text: string;
  /** Border-radius + border-style — the non-colour channel (WCAG 1.4.1). */
  shape: string;
  /** The existing emoji glyph. */
  glyph: string;
  /** Icon companion to the glyph. */
  icon: LucideIcon;
  /** Short value label; consumers render `i18n._(label)`. */
  label: MessageDescriptor;
  /** `true` when the value carries no accent. */
  isQuiet: boolean;
}

const STYLES: Record<IndicatorValue, IndicatorStyle> = {
  good: {
    value: 'good',
    chip: 'bg-capacity-normal/15 border-capacity-normal/40',
    text: 'text-capacity-normal-on-surface',
    shape: 'rounded-full border-2 border-solid', // circle
    glyph: '😊',
    icon: Smile,
    label: msg({ id: 'settlingIn.progress.value.good', message: 'Good' }),
    isQuiet: false,
  },
  neutral: {
    value: 'neutral',
    chip: 'bg-capacity-warning/15 border-capacity-warning/40',
    text: 'text-capacity-warning-on-surface',
    shape: 'rounded-md border-2 border-solid', // square
    glyph: '😐',
    icon: Meh,
    label: msg({ id: 'settlingIn.progress.value.neutral', message: 'Neutral' }),
    isQuiet: false,
  },
  bad: {
    value: 'bad',
    chip: 'bg-capacity-critical/15 border-capacity-critical/40',
    text: 'text-capacity-critical-on-surface',
    shape: 'rounded-none border-2 border-solid', // sharp
    glyph: '☹️',
    icon: Frown,
    label: msg({ id: 'settlingIn.progress.value.bad', message: 'Bad' }),
    isQuiet: false,
  },
  unset: {
    value: 'unset',
    chip: 'bg-muted border-border',
    text: 'text-muted-foreground',
    shape: 'rounded-md border-2 border-dashed', // dashed — never mistakable for neutral
    glyph: '—',
    icon: Minus,
    label: msg({ id: 'settlingIn.progress.value.unset', message: 'Not assessed' }),
    isQuiet: true,
  },
};

/** Resolve a tri-state value to its full set of theme-aware classes and glyphs. */
export function getIndicatorStyle(value: IndicatorValue): IndicatorStyle {
  return STYLES[value];
}
